import { Signal, SeatingChart, Seat, Permit, Snapshot } from "@ticketmachine/sdk";
import client, { replaceURLParams } from "@/utils/http";
import { AxiosResponse } from "axios";
import { createConsumer } from "@rails/actioncable";
import { decode } from "universal-base64url";
import { derived, Readable } from "svelte/store";
import { RawPriceData } from "./resolve";
import { URLS } from "./urls";
import { AnchorType, FocusType } from "@/seating_chart_editor/viewport/seating-chart";

interface SeatingChartInfo {
    websocket: string,
    bbox: {width: number, height: number},
    blocks: number[],
    colors: {[id: string]: string},
    focus: {
        anchorType: AnchorType,
        focusType: FocusType,
        x: number,
        y: number
    }
}

const chartConnections: Map<number, Signal<Seat>> = new Map();


class Channel<T> implements Signal<T> {
    private _listeners: Map<number, (value: T) => void> = new Map();
    private _lid: number = 0;

    private _connect: (subscriber: (value: T) => void) => void;
    private _disconnect: () => void;

    constructor(connect: (subscriber: (value: T) => void) => void, disconnect: () => void) {
        this._connect = connect;
        this._disconnect = disconnect;
    }

    static create<T>(subscribe: (broadcast: (value: T) => void) => (() => void)): Channel<T> {
        let currentDisconnect: () => void = () => {};

        return new Channel(
            (s) => { currentDisconnect = subscribe(s); },
            () => { 
                currentDisconnect()
                currentDisconnect = () => {}
            }
        );
    }

    static asyncCreate<T>(subscribe: (broadcast: (value: T) => void) => Promise<() => void>): Channel<T> {

        return Channel.create((set) => {
            let cancel = false;
            let currentDisconnect = () => { cancel = true; };

            (async () => {
                const disconnector = await subscribe((v) => {
                    if (!cancel) set(v);
                });

                if (cancel) {
                    disconnector();
                    return;
                }

                currentDisconnect = () => {
                    disconnector();
                    cancel = true;
                }
            })().catch(window.reportError);

            return () => currentDisconnect();
        });
    }

    subscribe(subscriber: (value: T) => void): () => void {
        const myLid = this._lid++;
        this._listeners.set(myLid, subscriber);
        if (this._listeners.size == 1) {
            this._connect((value) => {
                for (let l of this._listeners.values()) {
                    l(value);
                }
            });
        }

        return () => {
            this._listeners.delete(myLid);
            if (this._listeners.size == 0) {
                this._disconnect();
            }
        }
    }
}


const ANCHOR_MAP: Map<AnchorType, {x: "left"|"center"|"right", y: "top"|"middle"|"bottom"}> = new Map([
    [AnchorType.CUSTOM, {x: "left", y: "top"}],
    [AnchorType.TOP_LEFT, {x: "left", y: "top"}],
    [AnchorType.TOP_CENTER, {x: "center", y: "top"}],
    [AnchorType.TOP_RIGHT, {x: "right", y: "top"}],
    [AnchorType.MIDDLE_LEFT, {x: "left", y: "middle"}],
    [AnchorType.MIDDLE_CENTER, {x: "center", y: "middle"}],
    [AnchorType.MIDDLE_RIGHT, {x: "right", y: "middle"}],
    [AnchorType.BOTTOM_LEFT, {x: "left", y: "bottom"}],
    [AnchorType.BOTTOM_CENTER, {x: "center", y: "bottom"}],
    [AnchorType.BOTTOM_RIGHT, {x: "right", y: "bottom"}],
])


export default function createSeatingChartConnector(snapshot: Readable<Snapshot>, updateCart: () => Promise<void>, getPriceDataCached: (id: number) => Promise<RawPriceData>, urls: URLS) {
    return {
        /**
         * Gets the data about a seating chart.
         */
        async getSeatingChart(event_id: number): Promise<SeatingChart> {
            const RawChart = await (client.get(replaceURLParams(urls.GetChartURL, {id: event_id})) as Promise<AxiosResponse<SeatingChartInfo>>);
            const data = RawChart.data;

            const background = new URL(replaceURLParams(urls.ChartBackgroundURL, {id: event_id}), window.location.origin);

            const focus = {
                anchor: ANCHOR_MAP.get(data.focus.anchorType)!,
                type: data.focus.focusType == FocusType.PUSH ? "PUSH" as "PUSH" : "PULL" as "PULL",
                dx: data.focus.anchorType === AnchorType.CUSTOM ? data.focus.x : 0,
                dy: data.focus.anchorType === AnchorType.CUSTOM ? data.focus.y : 0
            }

            return {
                blocks: data.blocks,
                colors: data.colors,
                bbox: data.bbox,
                focus,
                background_url: background.toString()
            }
        },

        /**
         * Returns the seats of a seating chart.
         */
        async getSeats(event_id: number, block_id: number): Promise<Seat[]> {
            return (<Seat[]>(await client.get(replaceURLParams(urls.GetBlockURL, {id: event_id, block_id}))).data.seats).map(s => ({
                ...s,
                block_id
            }));
        },

        /**
         * Returns changes about a seat.
         */
        seatUpdated(event_id: number): Signal<Seat> {
            if (!chartConnections.has(event_id)) {
                chartConnections.set(event_id, Channel.asyncCreate(async (set) => {
                    const info = await client.get(replaceURLParams(urls.GetChartURL, {id: event_id})) as AxiosResponse<SeatingChartInfo>;

                    const parsed = new URLSearchParams(info.data.websocket);
                    const channel = JSON.parse(decode(parsed.get("channel")!));

                    const consumer = createConsumer(info.data.websocket);
                    consumer.subscriptions.create(channel, {
                        received(data: Seat) {
                            set(data);
                        }
                    });

                    return () => {
                        consumer.disconnect();
                    }
                }));
            }

            return chartConnections.get(event_id)!;
        },

        /**
         * Returns seat
         */
        blockedSeats(event_id: number): Signal<number[]> {
            return derived(
                snapshot,
                ($output) => (
                    $output
                    .items
                    .filter(s => s.type === "permit")
                    .map(s => s as Permit)
                    .filter(s => s.event_id === event_id)
                    .filter(s => s.seat !== null)
                    .map(s => Number(s.seat!.id))
                )
            );
        },

    }
};
